import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { getCustomerLedgerTransactions, getAllInvoiceDetails } from "@api";
import { useMerchant } from "@auth";
import { InvoiceDetailsModal } from "@components";
import { groupTransactions } from "@areas/trade-accounts/displays/utils/groupTransactions";
import { formatAmount, formatDateShortMonth, cn } from "@utils";
import AgedDebtCard from "../SmartNoteModal/AgedDebtCard";
import ActivityFeed from "../SmartNoteModal/ActivityFeed";
import DocumentsTab from "./DocumentsTab";
import BalanceInvoiceList from "./BalanceInvoiceList";
import PaymentProfilePicker from "./PaymentProfilePicker";
import ReadBlock from "./ReadBlock";

const TABS = [
  { key: "summary", label: "Summary" },
  { key: "balance", label: "Balance" },
  { key: "activity", label: "Activity" },
  { key: "documents", label: "Documents" },
  { key: "account", label: "Account" },
];

// Left-aligned underline tab bar (index-based), kept in sync with the swipeable panels below.
function TabBar({ active, onChange }) {
  return (
    <div className="flex gap-6 border-b border-zinc-200 px-5">
      {TABS.map((tab, i) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(i)}
          className={cn(
            "-mb-px border-b-2 py-3 text-sm font-medium transition-colors",
            active === i
              ? "border-zinc-900 text-zinc-900"
              : "border-transparent text-zinc-500 hover:text-zinc-800",
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-zinc-200 px-6 py-12 text-center">
      {Icon && <Icon aria-hidden="true" className="size-7 text-zinc-300" />}
      <p className="text-sm font-medium text-zinc-700">{title}</p>
      {hint && <p className="max-w-xs text-xs text-zinc-400">{hint}</p>}
    </div>
  );
}

// A green-to-red ramp that deepens with age, in the same family as the Balance tab's bucket dots.
const AGED_DEBT_COLORS = {
  "this-month": "bg-emerald-500",
  "1mo": "bg-amber-400",
  "2mo": "bg-orange-500",
  "3mo-plus": "bg-rose-600",
};

function SummaryTab({ customer, detail, isLoading, onReadReady, onReadRevealed }) {
  return (
    <div className="flex flex-col gap-4">
      <AgedDebtCard customer={detail} isLoading={isLoading} colors={AGED_DEBT_COLORS} />
      <ReadBlock customerId={customer.id} onReadyChange={onReadReady} onRevealed={onReadRevealed} />
    </div>
  );
}

function BalanceTab({ customer, detail }) {
  const [search, setSearch] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const { merchant } = useMerchant();
  const accountCode = detail?.accountCode ?? customer?.accountCode;

  const { data: transactions } = useQuery({
    queryKey: ["ledgerTransactions", accountCode],
    queryFn: () => getCustomerLedgerTransactions(accountCode),
    enabled: !!accountCode,
    staleTime: 5 * 60 * 1000,
  });

  const invoiceGroups = useMemo(
    () => (transactions ? groupTransactions(transactions) : null),
    [transactions],
  );

  const invoiceNumbers = useMemo(
    () =>
      (invoiceGroups ?? [])
        .flatMap((g) => g.invoices)
        .map((inv) => inv.transactionNumber)
        .filter(Boolean),
    [invoiceGroups],
  );

  const { data: invoiceDetailsArray } = useQuery({
    queryKey: ["allInvoiceDetails", customer?.id, invoiceNumbers],
    queryFn: () => getAllInvoiceDetails(customer.id, invoiceNumbers),
    enabled: !!customer?.id && invoiceNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const invoiceDetailsMap = useMemo(() => {
    if (!invoiceDetailsArray) return {};
    return Object.fromEntries(invoiceDetailsArray.map((d) => [d.number, d]));
  }, [invoiceDetailsArray]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoiceGroups;
    const amountQuery = q.replace(/[£,\s-]/g, "");
    const isAmountQuery = /^\d+(\.\d{0,2})?$/.test(amountQuery);
    const matches = (inv) => {
      const details = invoiceDetailsMap[inv.transactionNumber];
      const address = details?.deliveryAddress;
      const text = [
        inv.transactionNumber,
        details?.customerReference,
        address?.line1,
        address?.line2,
        address?.city,
        address?.postcode,
      ];
      if (text.some((field) => field?.toLowerCase().includes(q))) return true;
      if (!isAmountQuery) return false;
      return [inv.amount, inv.initialAmount, details?.grossAmount]
        .filter((n) => n != null)
        .some((n) => Math.abs(n).toFixed(2).startsWith(amountQuery));
    };
    return (invoiceGroups ?? [])
      .map((group) => ({ ...group, invoices: group.invoices.filter(matches) }))
      .filter((group) => group.invoices.length > 0);
  }, [invoiceGroups, invoiceDetailsMap, search]);

  const hasInvoices = (invoiceGroups?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <MagnifyingGlassIcon
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search invoice number, reference, address or amount"
          className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {hasInvoices ? (
        <div className="flex flex-col gap-3">
          {search && (filteredGroups?.length ?? 0) === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-zinc-400">
              No invoices match “{search}”.
            </p>
          ) : (
            <BalanceInvoiceList
              groups={filteredGroups}
              invoiceDetailsMap={invoiceDetailsMap}
              expandAll={!!search.trim()}
              onOpenDetails={setSelectedInvoice}
            />
          )}
        </div>
      ) : (
        <EmptyState
          icon={DocumentTextIcon}
          title="No open invoices"
          hint="Invoices with an outstanding balance will appear here grouped by due date."
        />
      )}

      <InvoiceDetailsModal
        invoice={selectedInvoice}
        isOpen={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        orgId={merchant?.organisationId}
      />
    </div>
  );
}

function ActivityTab({
  customer,
  scrollContainerRef,
  enterId,
  enterDelay,
  onPreviewStep,
  onOpenPaymentRequest,
}) {
  return (
    <div className="-mx-2">
      <ActivityFeed
        customerId={customer.id}
        scrollContainerRef={scrollContainerRef}
        enterId={enterId}
        enterDelay={enterDelay}
        spacious
        onPreviewStep={onPreviewStep}
        onOpenPaymentRequest={onOpenPaymentRequest}
      />
    </div>
  );
}

function AccountRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-zinc-100 py-3.5 last:border-b-0">
      <span className="text-sm text-zinc-500">{label}</span>
      <span className="text-right text-sm font-medium text-zinc-900">{children}</span>
    </div>
  );
}

function AccountSection({ children }) {
  return <div className="rounded-2xl border border-zinc-200 bg-white px-4">{children}</div>;
}

function latestBy(items, dateKey) {
  return (items ?? []).reduce(
    (latest, x) => (!latest || new Date(x[dateKey]) > new Date(latest[dateKey]) ? x : latest),
    null,
  );
}

function AccountTab({ customer, detail }) {
  const creditLimit = detail?.creditLimit ?? customer?.creditLimit;
  const balance = detail?.balance ?? customer?.balance;
  const groups = detail?.groups ?? customer?.groups ?? [];
  const branch = detail?.branch ?? customer?.branch;
  const lastPayment = latestBy(
    detail?.payments?.filter((p) => p.status === "PaymentSucceeded"),
    "initialisedOn",
  );
  const lastRequest = latestBy(detail?.paymentRequests, "requestedOn");
  const dash = <span className="text-zinc-300">—</span>;

  const available = creditLimit != null && balance != null ? creditLimit - balance : null;

  const amountOn = (amount, date) => (
    <>
      {formatAmount(amount)}
      <span className="ml-1.5 font-normal text-zinc-400">{formatDateShortMonth(date)}</span>
    </>
  );

  return (
    <div className="space-y-4">
      <AccountSection>
        <AccountRow label="Credit limit">
          {creditLimit != null ? formatAmount(creditLimit) : dash}
        </AccountRow>
        <AccountRow label="Credit remaining">
          {available == null ? (
            dash
          ) : available < 0 ? (
            <span className="text-red-600">{formatAmount(available)}</span>
          ) : (
            formatAmount(available)
          )}
        </AccountRow>
      </AccountSection>
      <AccountSection>
        <AccountRow label="Branch">{branch || dash}</AccountRow>
        <AccountRow label="Payment profile">
          <PaymentProfilePicker
            customerId={customer?.id}
            customerName={detail?.name ?? customer?.name}
            paymentProfileId={detail?.paymentProfileId ?? customer?.paymentProfileId}
          />
        </AccountRow>
        <AccountRow label="Groups">
          {groups.length > 0 ? (
            <span className="inline-flex flex-wrap justify-end gap-1">
              {groups.map((g) => (
                <span
                  key={g.id ?? g.groupName}
                  className="rounded-md border border-primary-200 px-2 py-0.5 text-xs text-zinc-700"
                >
                  {g.groupName}
                </span>
              ))}
            </span>
          ) : (
            dash
          )}
        </AccountRow>
      </AccountSection>
      <AccountSection>
        <AccountRow label="Last payment received">
          {lastPayment ? amountOn(lastPayment.amount, lastPayment.initialisedOn) : dash}
        </AccountRow>
        <AccountRow label="Last request sent">
          {lastRequest ? amountOn(lastRequest.amountRequested, lastRequest.requestedOn) : dash}
        </AccountRow>
      </AccountSection>
    </div>
  );
}

// Threshold (fraction of panel width) a swipe must pass to advance to the next view.
const SWIPE_COMMIT = 0.2;
// Movement (px) before a gesture is locked as horizontal (swipe) vs vertical (scroll).
const SWIPE_INTENT = 10;

const ACTIVITY_INDEX = TABS.findIndex((t) => t.key === "activity");
// Matches the panel slide (transition-transform duration below); the +80ms lets the slide fully
// settle before the new row unfurls, so the two read as a sequence rather than at once.
const SLIDE_MS = 450;
const ENTER_AFTER_SLIDE_MS = SLIDE_MS + 80;

export default function DetailTabs({
  customer,
  detail,
  isLoading,
  onReadReady,
  onReadRevealed,
  enterId,
  activityPulse,
  onPreviewStep,
  onOpenPaymentRequest,
}) {
  const count = TABS.length;
  const [index, setIndex] = useState(0);
  const [dragPx, setDragPx] = useState(0);
  const [dragging, setDragging] = useState(false);

  // When a suggestion is accepted the controller bumps activityPulse. Decide the row-entrance delay
  // SYNCHRONOUSLY on the render the pulse arrives (same render enterId updates) so the row never
  // flashes expanded before it animates: if we're already on Activity it unfurls at once; otherwise
  // it waits for the slide to finish, so the swipe and the entrance read as a clean sequence.
  const renderPulseRef = useRef(activityPulse);
  const enterDelayRef = useRef(0);
  if (activityPulse !== renderPulseRef.current) {
    enterDelayRef.current = index === ACTIVITY_INDEX ? 0 : ENTER_AFTER_SLIDE_MS;
    renderPulseRef.current = activityPulse;
  }

  // Slide to Activity only when the pulse actually CHANGES — not merely when it's non-zero. The
  // counter persists across opens (the controller outlives the modal), so a plain `> 0` check would
  // wrongly land a freshly-opened account on Activity; the ref starts at the current value so mount
  // never triggers a switch. New accounts therefore land on Summary, as intended.
  const switchPulseRef = useRef(activityPulse);
  useEffect(() => {
    if (activityPulse !== switchPulseRef.current) {
      switchPulseRef.current = activityPulse;
      setIndex(ACTIVITY_INDEX);
    }
  }, [activityPulse]);

  const viewportRef = useRef(null);
  const activityScrollRef = useRef(null);
  const documentsScrollRef = useRef(null);
  const gesture = useRef({ active: false, startX: 0, startY: 0, axis: null });

  const goTo = useCallback(
    (next) => setIndex(Math.max(0, Math.min(count - 1, next))),
    [count],
  );

  // Touch-only swipe: dragging follows the finger (with edge resistance), and on release snaps to
  // the next/previous view if past the commit threshold. Mouse users change view by clicking a tab,
  // which animates the same slide. touch-action: pan-y lets vertical scrolling inside a panel work;
  // we only hijack the gesture once it's clearly horizontal.
  const onPointerDown = (e) => {
    if (e.pointerType !== "touch") return;
    gesture.current = { active: true, startX: e.clientX, startY: e.clientY, axis: null };
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g.active) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.axis) {
      if (Math.abs(dx) < SWIPE_INTENT && Math.abs(dy) < SWIPE_INTENT) return;
      g.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (g.axis === "x") {
        setDragging(true);
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* capture best-effort */
        }
      }
    }
    if (g.axis !== "x") return;
    const atStart = index === 0 && dx > 0;
    const atEnd = index === count - 1 && dx < 0;
    setDragPx(atStart || atEnd ? dx / 3 : dx);
  };

  const endGesture = () => {
    const g = gesture.current;
    if (!g.active) return;
    if (g.axis === "x") {
      const width = viewportRef.current?.getBoundingClientRect().width ?? 1;
      if (dragPx <= -width * SWIPE_COMMIT) goTo(index + 1);
      else if (dragPx >= width * SWIPE_COMMIT) goTo(index - 1);
    }
    gesture.current = { active: false, startX: 0, startY: 0, axis: null };
    setDragPx(0);
    setDragging(false);
  };

  const panelWidth = 100 / count; // each panel is one slot of the wider track

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TabBar active={index} onChange={goTo} />
      <div
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        className="relative min-h-0 flex-1 overflow-hidden"
        style={{ touchAction: "pan-y" }}
      >
        <div
          className={cn("flex h-full", !dragging && "transition-transform duration-[450ms] ease-out")}
          style={{
            width: `${count * 100}%`,
            transform: `translateX(calc(${-index * panelWidth}% + ${dragPx}px))`,
          }}
        >
          <div className="h-full overflow-y-auto px-5 py-5" style={{ width: `${panelWidth}%` }}>
            <SummaryTab
              customer={customer}
              detail={detail}
              isLoading={isLoading}
              onReadReady={onReadReady}
              onReadRevealed={onReadRevealed}
            />
          </div>
          <div className="h-full overflow-y-auto px-5 py-5" style={{ width: `${panelWidth}%` }}>
            <BalanceTab customer={customer} detail={detail} />
          </div>
          <div
            ref={activityScrollRef}
            className="h-full overflow-y-auto px-5 py-5"
            style={{ width: `${panelWidth}%` }}
          >
            <ActivityTab
              customer={customer}
              scrollContainerRef={activityScrollRef}
              enterId={enterId}
              enterDelay={enterDelayRef.current}
              onPreviewStep={onPreviewStep}
              onOpenPaymentRequest={onOpenPaymentRequest}
            />
          </div>
          <div
            ref={documentsScrollRef}
            className="h-full overflow-y-auto px-5 py-5"
            style={{ width: `${panelWidth}%` }}
          >
            <DocumentsTab
              customer={customer}
              detail={detail}
              scrollContainerRef={documentsScrollRef}
            />
          </div>
          <div className="h-full overflow-y-auto px-5 py-5" style={{ width: `${panelWidth}%` }}>
            <AccountTab customer={customer} detail={detail} />
          </div>
        </div>
      </div>
    </div>
  );
}
